import { create } from 'zustand';
import api, { queryClient } from '../api';
import { v4 as uuidv4 } from 'uuid';

const FileUploadStore = create((set) => ({

  progress: 0,
  fileLoading: false,
  fileInputRef: null,
  showUpload: false,
  isUploading: false,
  isUploadVisible: false,
  uploadMainRef: null,
  setFilePath: (value) => set({ filePath: value }),
  setFileSize: (size) => set({ fileSize: size }),
  setProgress: (value) => set({ progress: value }),
  setFileLoading: (value) => set({ fileLoading: value }),
  setIsUploadVisible: (value) => set({ isUploadVisible: value }),
  setIsUploading: (value) => set({ isUploading: value }),
  setShowUpload: (value) => set({ showUpload: value }),
  setInputFileName: (value) => set({ inputFileName: value }),
  setFileUrl: (value) => set({ fileUrl: value }),
}));

export const FileStore = create(set => ({
  files: [],
  new_chat_files: [],
  setNewFiles: (files) => set({ new_chat_files: Array.isArray(files) ? files : [] }),
  addFile: (file) => set(state => {
    return { files: [...state.files, file] }
  }),
  removeFile: (file) => set(state => ({ files: state.files.filter(f => f !== file) })),
  setFiles: (filesOrFn) => set(state => {
    const resolved = typeof filesOrFn === 'function' ? filesOrFn(state.files) : filesOrFn;
    return { files: Array.isArray(resolved) ? resolved : [] };
  }),
  _uploadCount: 0,
  fileLoading: false,
  progress: 0,
  incrementUploading: () => set(state => ({
    _uploadCount: state._uploadCount + 1,
    fileLoading: true,
  })),
  decrementUploading: () => set(state => {
    const next = Math.max(0, state._uploadCount - 1);
    return { _uploadCount: next, fileLoading: next > 0 };
  }),
  setFileLoading: (value) => set({ fileLoading: value }),
  setProgress: (value) => set({ progress: value }),
}))

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export const matchPendingUploads = (selectedFiles, uploads) => {
  const pending = Object.values(uploads || {}).filter(
    (upload) => upload.status !== 'UPLOADED'
  );
  if (selectedFiles.length !== pending.length) return null;
  const remaining = [...selectedFiles];
  const matches = [];
  for (const upload of pending) {
    const index = remaining.findIndex((file) => (
      file.name === upload.filename
      && file.size === upload.size_bytes
      && (file.type || 'application/octet-stream') === upload.mime_type
    ));
    if (index === -1) return null;
    matches.push({ file: remaining[index], uploadId: upload.upload_id });
    remaining.splice(index, 1);
  }
  return matches;
};

export const SpectraUploadStore = create((set, get) => ({
  uploads: {},
  start: async ({
    spaceId,
    files,
    finalizeId = uuidv4(),
    finalizeUploadIds,
  }) => {
    const entries = files.map((item) => ({
      ...item,
      uploadId: item.uploadId || uuidv4(),
      status: 'PENDING',
      progress: 0,
      error: null,
    }));
    const allUploadIds = finalizeUploadIds || entries.map((entry) => entry.uploadId);
    set((state) => ({
      uploads: {
        ...state.uploads,
        [spaceId]: {
          status: 'UPLOADING', files: entries, finalizeId, finalizeUploadIds: allUploadIds,
        },
      },
    }));

    try {
      for (const entry of entries) {
        let lastError;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const formData = new FormData();
            formData.append('file', entry.file);
            formData.append('space_id', spaceId);
            formData.append('upload_id', entry.uploadId);
            await api({
              method: 'POST',
              url: '/storage/assets/upload',
              data: formData,
              onUploadProgress: (event) => {
                const progress = event.total
                  ? Math.round((event.loaded / event.total) * 100)
                  : 0;
                get().updateFile(spaceId, entry.uploadId, { progress, status: 'UPLOADING' });
              },
            });
            get().updateFile(spaceId, entry.uploadId, { progress: 100, status: 'UPLOADED' });
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            if (attempt < 2) await wait(500 * (2 ** attempt));
          }
        }
        if (lastError) throw lastError;
      }

      await api({
        method: 'POST',
        url: `/spectra/${spaceId}/finalize`,
        data: {
          finalize_id: finalizeId,
          upload_ids: allUploadIds,
        },
      });
      set((state) => ({
        uploads: { ...state.uploads, [spaceId]: { ...state.uploads[spaceId], status: 'READY' } },
      }));
      await queryClient.invalidateQueries({ queryKey: ['SPECTRAS'] });
    } catch (error) {
      set((state) => ({
        uploads: {
          ...state.uploads,
          [spaceId]: { ...state.uploads[spaceId], status: 'CREATE_FAILED', error: error.message },
        },
      }));
      throw error;
    }
  },
  retry: (spaceId) => {
    const upload = get().uploads[spaceId];
    if (!upload) return Promise.resolve();
    return get().start({
      spaceId,
      files: upload.files,
      finalizeId: upload.finalizeId,
      finalizeUploadIds: upload.finalizeUploadIds,
    });
  },
  updateFile: (spaceId, uploadId, updates) => set((state) => ({
    uploads: {
      ...state.uploads,
      [spaceId]: {
        ...state.uploads[spaceId],
        files: state.uploads[spaceId].files.map((entry) => (
          entry.uploadId === uploadId ? { ...entry, ...updates } : entry
        )),
      },
    },
  })),
}));

export default FileUploadStore;
